import { api } from './index';
import { ApiResponse, Organization, InviteUserRequest, InviteUserResponse, User } from '../types';

export const organizationService = {
  getOrganizations: async (): Promise<Organization[]> => {
    const response = await api.get<Organization[]>('/admin/organizations');
    return response.data;
  },
  getOrganization: async (organizationId: string): Promise<Organization> => {
    const response = await api.get<Organization>(`/admin/organizations/${organizationId}`);
    return response.data;
  },
  createOrganization: async (data: Partial<Organization>): Promise<ApiResponse<Organization>> => {
    const response = await api.post<ApiResponse<Organization>>('/admin/organizations', data);
    return response.data;
  },
  updateOrganization: async (organizationId: string, data: Partial<Organization>): Promise<ApiResponse<Organization>> => {
    const response = await api.put<ApiResponse<Organization>>(`/admin/organizations/${organizationId}`, data);
    return response.data;
  },
  deleteOrganization: async (organizationId: string): Promise<ApiResponse<void>> => {
    const response = await api.delete<ApiResponse<void>>(`/admin/organizations/${organizationId}`);
    return response.data;
  },
  deactivateOrganization: async (organizationId: string): Promise<ApiResponse<Organization>> => {
    const response = await api.patch<ApiResponse<Organization>>(`/admin/organizations/${organizationId}/deactivate`);
    return response.data;
  },
  activateOrganization: async (organizationId: string): Promise<ApiResponse<Organization>> => {
    const response = await api.patch<ApiResponse<Organization>>(`/admin/organizations/${organizationId}/activate`);
    return response.data;
  },
  deleteMultipleOrganizations: async (organizationIds: string[]): Promise<ApiResponse<void>> => {
    const response = await api.delete<ApiResponse<void>>('/admin/organizations', { data: { ids: organizationIds } });
    return response.data;
  },
  updateAnalysisCredits: async (organizationId: string, analysisCredits?: number) => {
    const response = await api.patch(`/admin/organizations/${organizationId}/service-limits`, {
      analysisCredits: analysisCredits === undefined ? null : analysisCredits
    });
    return response.data;
  },
};

export const userManagementService = {
  getAllUsers: async (): Promise<User[]> => {
    const response = await api.get<ApiResponse<User[]>>('/admin/users');
    return response.data.data;
  },
  getUsersByOrganization: async (organizationId: string): Promise<User[]> => {
    const response = await api.get<User[]>(`/admin/organizations/${organizationId}/users`);
    return response.data;
  },
  inviteUser: async (data: InviteUserRequest): Promise<InviteUserResponse> => {
    const response = await api.post<InviteUserResponse>('/admin/users/invite', data);
    return response.data;
  },
  updateUser: async (userId: string, data: { name?: string; role?: string; status?: string }): Promise<ApiResponse<User>> => {
    const response = await api.put<ApiResponse<User>>(`/admin/users/${userId}`, data);
    return response.data;
  },
  updateUserRole: async (userId: string, role: 'user' | 'admin'): Promise<ApiResponse<User>> => {
    const response = await api.put<ApiResponse<User>>(`/admin/users/${userId}`, { role });
    return response.data;
  },
  removeUserAppAccess: async (userId: string): Promise<ApiResponse<void>> => {
    const response = await api.post<ApiResponse<void>>(`/admin/users/${userId}/app-access/remove`);
    return response.data;
  },
  restoreUserAppAccess: async (userId: string): Promise<ApiResponse<void>> => {
    const response = await api.post<ApiResponse<void>>(`/admin/users/${userId}/app-access/restore`);
    return response.data;
  },
  getUserAppAssignments: async (userId: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/admin/users/${userId}/app-assignments`);
    return response.data;
  },
  removeUserFromOrganization: async (userId: string, organizationId: string): Promise<ApiResponse<void>> => {
    const response = await api.delete<ApiResponse<void>>(`/admin/organizations/${organizationId}/users/${userId}`);
    return response.data;
  },
  deleteUser: async (userId: string): Promise<ApiResponse<void>> => {
    const response = await api.delete<ApiResponse<void>>(`/admin/users/${userId}`);
    return response.data;
  },
  assignAdminToOrganization: async (organizationId: string): Promise<ApiResponse<any>> => {
    const response = await api.patch<ApiResponse<any>>(`/admin/assign-organization/${organizationId}`);
    return response.data;
  },
  returnToSuperOrganization: async (): Promise<ApiResponse<any>> => {
    const response = await api.patch<ApiResponse<any>>('/admin/return-to-super-organization');
    return response.data;
  }
};

export const adminService = {
  getSystemHealth: async (): Promise<{
    status: string;
    azure_ad_connection: boolean;
    database_connection: boolean;
    services: { azure_graph: boolean; token_validation: boolean; file_storage: boolean; };
    version: string;
    uptime: string;
    last_check: string;
  }> => {
    const response = await api.get<ApiResponse<{ status: string; azure_ad_connection: boolean; database_connection: boolean; services: { azure_graph: boolean; token_validation: boolean; file_storage: boolean; }; version: string; uptime: string; last_check: string; }>>('/admin/system/health');
    return response.data.data;
  },
  getSystemLogs: async (limit: number = 100): Promise<{ logs: Array<{ timestamp: string; level: string; message: string; user_id?: string; action?: string; details?: any; }>; total: number; limit: number; }> => {
    const response = await api.get<ApiResponse<{ logs: Array<{ timestamp: string; level: string; message: string; user_id?: string; action?: string; details?: any; }>; total: number; limit: number; }>>(`/admin/system/logs?limit=${limit}`);
    return response.data.data;
  },
  getDashboardStatus: async (): Promise<{ totalOrganizations: number; totalUsers: number; totalServiceProviders: number; activeAnalyses: number; completedAnalyses: number; openAnalyses: number; pendingAnalyses: number; totalServices: number; lastUpdated: string; }> => {
    const response = await api.get<ApiResponse<{ totalOrganizations: number; totalUsers: number; totalServiceProviders: number; activeAnalyses: number; completedAnalyses: number; openAnalyses: number; pendingAnalyses: number; totalServices: number; lastUpdated: string; }>>('/admin/dashboard/status');
    return response.data.data;
  },
  resetContractPackageStatus: async (contractPackageId: string): Promise<ApiResponse<any>> => {
    const response = await api.patch<ApiResponse<any>>(`/admin/contract-packages/${contractPackageId}/reset-status`);
    return response.data;
  },
  downloadContractPackageJson: async (contractPackageId: string): Promise<Blob> => {
    const response = await api.get(`/admin/contract-packages/${contractPackageId}/results/json`, {
      responseType: 'blob',
      headers: { 'Accept': 'application/json' }
    });
    const blob = response.data;
    const disposition: string | undefined = response.headers['content-disposition'];
    let filename = `output_complete_${contractPackageId}.json`;
    if (disposition && disposition.includes('filename=')) {
      const match = disposition.match(/filename="?([^";]+)"?/);
      if (match && match[1]) filename = decodeURIComponent(match[1]);
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    return blob;
  },
};


